/** @file Elbow solve for a non-uniform parent: sampled reach range, refined extremes, bisected root. */
import { trig } from '@pose2d/math/trig';
import type { Affine } from '@pose2d/math/types';

/** Intervals of |psi| over [0, pi] (SAMPLES + 1 points); an extreme between samples is refined by golden section. */
const SAMPLES = 64;
const GOLDEN_STEPS = 40;
/** 52 halvings of a pi/64 bracket end below 1e-16 rad. */
const BISECT_STEPS = 52;
const INV_PHI = (Math.sqrt(5) - 1) / 2;
/** |psi| of each sample (the two extremes moved to their refined position) and the reach there; range() fills, root() reads. */
const PSI = new Float64Array(SAMPLES + 1);
const REACH = new Float64Array(SAMPLES + 1);
/** Sample index of the refined maximum [0] and minimum [1] of the last range(); root() walks from one to the other. */
const EXTREME = new Int32Array(2);

/**
 * The chain being solved and every number passed between the steps below, as module scratch sized once. No number
 * crosses a function call inside the scan (only sample indices and ±1, which are small integers), and reach() and
 * hypot() call nothing but Math intrinsics, so a solve allocates nothing whatever the JIT inlines (final review m21:
 * before, ~21 KB of boxed doubles and Math.hypot arrays per solve).
 */
const CHAIN = new Float64Array(17);
/** The parent's local linear part at rotation 0 (a, b, c, d of math/affine). */
const A = 0;
const B = 1;
const C = 2;
const D = 3;
/** The child joint e, |e|, |childLen|·|det| and 1 when elbowSpan's degenerate case (det or |e| zero) applies. */
const EX = 4;
const EY = 5;
const L1 = 6;
const K = 7;
const DEGENERATE = 8;
const BEND = 9;
/** reach() input |psi| and output; root()'s target reach; golden()/bisect() result; hypot() inputs and output. */
const AT = 10;
const REACHED = 11;
const DD = 12;
const FOUND = 13;
const HX = 14;
const HY = 15;
const H = 16;

/**
 * CHAIN[H] := hypot(CHAIN[HX], CHAIN[HY]) by trig.hypot's formula (Infinity over NaN, then the sum of squares scaled by
 * the larger magnitude, bit-identical to Math.hypot). It stays inline because no number crosses a call inside the scan
 * (boxed doubles, m21); it must change together with `@pose2d/math/trig`'s hypot.
 */
function hypot(): void {
  const ax = Math.abs(CHAIN[HX] ?? 0);
  const ay = Math.abs(CHAIN[HY] ?? 0);
  const max = ax > ay ? ax : ay;
  if (ax === Number.POSITIVE_INFINITY || ay === Number.POSITIVE_INFINITY) CHAIN[H] = Number.POSITIVE_INFINITY;
  else if (Number.isNaN(ax) || Number.isNaN(ay)) CHAIN[H] = Number.NaN;
  else if (max === 0) CHAIN[H] = 0;
  else CHAIN[H] = Math.sqrt((ax / max) * (ax / max) + (ay / max) * (ay / max)) * max;
}

/** Makes (m, e, childLen, bend) the chain every step below reads. */
function load(m: Affine, ex: number, ey: number, childLen: number, bend: number): void {
  for (let i = 0; i < 4; i++) CHAIN[A + i] = m[i] ?? 0;
  const det = (CHAIN[A] ?? 0) * (CHAIN[D] ?? 0) - (CHAIN[B] ?? 0) * (CHAIN[C] ?? 0);
  CHAIN[EX] = ex;
  CHAIN[EY] = ey;
  CHAIN[HX] = ex;
  CHAIN[HY] = ey;
  hypot();
  CHAIN[L1] = CHAIN[H] ?? 0;
  CHAIN[K] = Math.abs(childLen) * Math.abs(det);
  CHAIN[DEGENERATE] = det === 0 || CHAIN[L1] === 0 ? 1 : 0;
  CHAIN[BEND] = bend;
}

/**
 * CHAIN[REACHED] := distance from the parent joint to the chain tip with the elbow bent by bend · CHAIN[AT] radians:
 * elbowSpan's child length (same operations, by hand so no number crosses a call), then the tip distance.
 */
function reach(): void {
  const psi = (CHAIN[BEND] ?? 0) * (CHAIN[AT] ?? 0);
  const cs = trig.cos(psi);
  const sn = trig.sin(psi);
  let span = 0;
  if (CHAIN[DEGENERATE] === 0) {
    const ex = CHAIN[EX] ?? 0;
    const ey = CHAIN[EY] ?? 0;
    const l1 = CHAIN[L1] ?? 0;
    const ux = (ex * cs - ey * sn) / l1;
    const uy = (ex * sn + ey * cs) / l1;
    CHAIN[HX] = (CHAIN[D] ?? 0) * ux - (CHAIN[B] ?? 0) * uy;
    CHAIN[HY] = (CHAIN[A] ?? 0) * uy - (CHAIN[C] ?? 0) * ux;
    hypot();
    span = (CHAIN[K] ?? 0) / (CHAIN[H] ?? 0);
  }
  CHAIN[HX] = (CHAIN[L1] ?? 0) + span * cs;
  CHAIN[HY] = span * sn;
  hypot();
  CHAIN[REACHED] = CHAIN[H] ?? 0;
}

/** CHAIN[FOUND] := golden-section maximum of sign·reach between the neighbours of sample k. */
function golden(k: number, sign: number): void {
  let a = PSI[Math.max(k - 1, 0)] ?? 0;
  let b = PSI[Math.min(k + 1, SAMPLES)] ?? 0;
  let x1 = b - INV_PHI * (b - a);
  let x2 = a + INV_PHI * (b - a);
  CHAIN[AT] = x1;
  reach();
  let f1 = sign * (CHAIN[REACHED] ?? 0);
  CHAIN[AT] = x2;
  reach();
  let f2 = sign * (CHAIN[REACHED] ?? 0);
  for (let i = 0; i < GOLDEN_STEPS; i++) {
    const right = f1 < f2;
    if (right) {
      a = x1;
      x1 = x2;
      f1 = f2;
      x2 = a + INV_PHI * (b - a);
    } else {
      b = x2;
      x2 = x1;
      f2 = f1;
      x1 = b - INV_PHI * (b - a);
    }
    CHAIN[AT] = right ? x2 : x1;
    reach();
    const f = sign * (CHAIN[REACHED] ?? 0);
    if (right) f2 = f;
    else f1 = f;
  }
  CHAIN[FOUND] = (a + b) / 2;
}

/**
 * Refines every sample-local extreme of sign·reach (sign 1 max, -1 min) by golden section between its neighbours, so a
 * narrow peak whose samples sit below another sample is not missed; moves the most extreme one into its sample and
 * returns that sample's index.
 */
function extreme(sign: number): number {
  let best = 0;
  let bestX = 0;
  let bestF = Number.NEGATIVE_INFINITY;
  for (let k = 0; k <= SAMPLES; k++) {
    const f = sign * (REACH[k] ?? 0);
    if ((k > 0 && sign * (REACH[k - 1] ?? 0) >= f) || (k < SAMPLES && sign * (REACH[k + 1] ?? 0) > f)) continue;
    golden(k, sign);
    const x = CHAIN[FOUND] ?? 0;
    CHAIN[AT] = x;
    reach();
    const g = sign * (CHAIN[REACHED] ?? 0);
    const top = g > f ? g : f;
    if (top <= bestF) continue;
    best = k;
    bestX = g > f ? x : (PSI[k] ?? 0);
    bestF = top;
  }
  PSI[best] = bestX;
  REACH[best] = sign * bestF;
  return best;
}

/** CHAIN[FOUND] := the |psi| between samples i and i + step where reach = CHAIN[DD], by bisection. */
function bisect(i: number, step: number): void {
  const dd = CHAIN[DD] ?? 0;
  const f0 = (REACH[i] ?? 0) - dd;
  let a = PSI[i] ?? 0;
  let b = PSI[i + step] ?? 0;
  for (let n = 0; n < BISECT_STEPS; n++) {
    const mid = (a + b) / 2;
    CHAIN[AT] = mid;
    reach();
    if (((CHAIN[REACHED] ?? 0) - dd) * f0 > 0) a = mid;
    else b = mid;
  }
  CHAIN[FOUND] = (a + b) / 2;
}

/**
 * Two-phase elbow solve for a parent whose local frame is not a similarity (spec §6.5, non-uniform parent scale).
 * Both phases share one record `io`: range() writes [0] min reach and [1] max reach, the caller writes [2] the
 * target reach, root() writes [3] |psi|; numbers go in and out through it, so the boundary boxes nothing either.
 */
export const elbowScan = {
  /** Samples the bend side (+1 / -1), writes io[0] = min reach and io[1] = max reach; root() must follow for the same chain. */
  range(io: Float64Array, m: Affine, ex: number, ey: number, childLen: number, bend: number): Float64Array {
    load(m, ex, ey, childLen, bend);
    for (let i = 0; i <= SAMPLES; i++) {
      const x = (Math.PI * i) / SAMPLES;
      PSI[i] = x;
      CHAIN[AT] = x;
      reach();
      REACH[i] = CHAIN[REACHED] ?? 0;
    }
    const kMax = extreme(1);
    const kMin = extreme(-1);
    EXTREME[0] = kMax;
    EXTREME[1] = kMin;
    io[0] = REACH[kMin] ?? 0;
    io[1] = REACH[kMax] ?? 0;
    return io;
  },
  /**
   * io[3] := |psi| (radians) of the elbow whose reach is io[2] on the branch from the last range()'s maximum to its
   * minimum (R-C7: continuous in the reach; the least-bent root when the reach falls monotonically); outside it, the
   * nearer extreme.
   */
  root(io: Float64Array, m: Affine, ex: number, ey: number, childLen: number, bend: number): Float64Array {
    load(m, ex, ey, childLen, bend);
    const dd = io[2] ?? 0;
    CHAIN[DD] = dd;
    const kMax = EXTREME[0] ?? 0;
    const kMin = EXTREME[1] ?? 0;
    const step = kMin < kMax ? -1 : 1;
    for (let i = kMax; i !== kMin; i += step) {
      if (((REACH[i] ?? 0) - dd) * ((REACH[i + step] ?? 0) - dd) > 0) continue;
      bisect(i, step);
      io[3] = CHAIN[FOUND] ?? 0;
      return io;
    }
    io[3] = (dd > (REACH[kMax] ?? 0) ? PSI[kMax] : PSI[kMin]) ?? 0;
    return io;
  },
};
